import { z } from 'zod';

/**
 * Versioned domain schemas.
 *
 * `CURRENT_SCHEMA_VERSION` is the version written by this build. Older documents are
 * upgraded by `migrateProjectDocument` (see ./migrations.ts) before they are validated
 * against these schemas, so every schema here describes the *current* shape only.
 */
export const CURRENT_SCHEMA_VERSION = 8 as const;

export const BPM_MIN = 60;
export const BPM_MAX = 200;
export const NAME_MAX = 80;

/** Sequencer grid: 16th notes, 4/4 time. */
export const STEPS_PER_BAR = 16;
export const PATTERN_LENGTHS = [16, 32, 64] as const;
export const MAX_PATTERNS = 64;
export const MAX_NOTES_PER_TRACK = 512;
export const MAX_SECTIONS = 32;
export const MAX_SECTION_BARS = 32;
/** Upper bound on a whole song; keeps playback scheduling and WAV export bounded. */
export const MAX_SONG_BARS = 256;

export const IdSchema = z.string().min(1).max(64).regex(/^[A-Za-z0-9_-]+$/, 'Invalid identifier');
export const IsoDateSchema = z.iso.datetime({ offset: true });
export const HexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Expected a #rrggbb colour');
export const NameSchema = z.string().trim().min(1, 'Name is required').max(NAME_MAX);
export const BpmSchema = z.number().int().min(BPM_MIN).max(BPM_MAX);

// ---------------------------------------------------------------- Music

export const InstrumentSchema = z.enum(['kick', 'snare', 'clap', 'hats', 'perc', 'bass808', 'cowbell', 'lead', 'pad']);
export type Instrument = z.infer<typeof InstrumentSchema>;
export const INSTRUMENTS: readonly Instrument[] = InstrumentSchema.options;
export const DRUM_INSTRUMENTS: readonly Instrument[] = ['kick', 'snare', 'clap', 'hats', 'perc'];
export const MELODIC_INSTRUMENTS: readonly Instrument[] = ['bass808', 'cowbell', 'lead', 'pad'];
export const isDrum = (instrument: Instrument): boolean => DRUM_INSTRUMENTS.includes(instrument);

export const MidiSchema = z.number().int().min(0).max(127);

/**
 * One note on the step grid. `step` is the 16th-note slot inside the pattern, `length`
 * its duration in steps (fractions allowed), `offset` a micro-timing nudge in fractions
 * of a step (−0.5 … +0.5). Drums use `pitch` for tuning where the instrument supports it.
 */
export const NoteEventSchema = z.object({
  step: z.number().int().min(0).max(63),
  length: z.number().min(0.25).max(64),
  velocity: z.number().min(0.05).max(1),
  pitch: MidiSchema,
  offset: z.number().min(-0.5).max(0.5),
});
export type NoteEvent = z.infer<typeof NoteEventSchema>;

export const PatternSchema = z
  .object({
    id: IdSchema,
    name: NameSchema,
    lengthSteps: z.union([z.literal(16), z.literal(32), z.literal(64)]),
    tracks: z.partialRecord(InstrumentSchema, z.array(NoteEventSchema).max(MAX_NOTES_PER_TRACK)),
  })
  .superRefine((pattern, ctx) => {
    for (const [instrument, notes] of Object.entries(pattern.tracks)) {
      notes?.forEach((note, i) => {
        if (note.step >= pattern.lengthSteps) {
          ctx.addIssue({
            code: 'custom',
            path: ['tracks', instrument, i, 'step'],
            message: `Step ${note.step} is outside the ${pattern.lengthSteps}-step pattern`,
          });
        }
      });
    }
  });
export type Pattern = z.infer<typeof PatternSchema>;

export const TrackEffectsSchema = z.object({
  /** Distortion amount, 0 = bypassed. */
  drive: z.number().min(0).max(1),
  /** Low-pass filter cutoff; 20 kHz is effectively open. */
  cutoffHz: z.number().min(80).max(20000),
  /** Post-fader send levels into the shared delay and reverb buses. */
  delaySend: z.number().min(0).max(1),
  reverbSend: z.number().min(0).max(1),
});
export type TrackEffects = z.infer<typeof TrackEffectsSchema>;

// ---------------------------------------------------------------- Audio production limits

/** Per-file limit for imported samples and saved recordings. */
export const MAX_SAMPLE_BYTES = 20 * 1024 * 1024;
/** Longest sample accepted (decoded audio is held in memory while in use). */
export const MAX_SAMPLE_SECONDS = 180;
/** Total stored binaries (audio and, since Phase 3A, images) per project. */
/** Kept below the import limit (50 MB of embedded data) so every project can be exported and re-imported. */
export const MAX_PROJECT_AUDIO_BYTES = 45 * 1024 * 1024;
export const MAX_AUDIO_ASSETS = 100;
export const MAX_IMAGE_ASSETS = 100;
export const MAX_AUDIO_CLIPS = 128;
export const AUDIO_LANES = 4;
export const MAX_CLIP_LOOPS = 16;
export const MAX_FADE_SECONDS = 30;
export const CHOKE_GROUPS = 4;

/**
 * Sample instrument on a drum track. `null` on the track means the synthesized voice.
 * Pitch shifts by changing playback rate (speed and pitch change together).
 */
export const SampleSlotSchema = z
  .object({
    assetId: IdSchema,
    gainDb: z.number().min(-24).max(12),
    pitchSemitones: z.number().int().min(-24).max(24),
    trimStartSec: z.number().min(0).max(MAX_SAMPLE_SECONDS),
    /** null = play to the end of the file. */
    trimEndSec: z.number().min(0).max(MAX_SAMPLE_SECONDS).nullable(),
    reverse: z.boolean(),
    /** 0 = none; hits in the same group cut each other off (e.g. open/closed hats). */
    chokeGroup: z.number().int().min(0).max(CHOKE_GROUPS),
  })
  .refine((s) => s.trimEndSec === null || s.trimEndSec > s.trimStartSec, {
    message: 'Trim end must be after trim start',
    path: ['trimEndSec'],
  });
export type SampleSlot = z.infer<typeof SampleSlotSchema>;

export const TrackSchema = z.object({
  id: IdSchema,
  name: NameSchema,
  instrument: InstrumentSchema,
  volumeDb: z.number().min(-60).max(6),
  pan: z.number().min(-1).max(1),
  muted: z.boolean(),
  solo: z.boolean(),
  effects: TrackEffectsSchema,
  sample: SampleSlotSchema.nullable(),
});
export type Track = z.infer<typeof TrackSchema>;

export const SectionKindSchema = z.enum(['intro', 'verse', 'buildup', 'drop', 'breakdown', 'outro']);
export type SectionKind = z.infer<typeof SectionKindSchema>;
export const SECTION_KINDS: readonly SectionKind[] = SectionKindSchema.options;

export const SectionSchema = z.object({
  id: IdSchema,
  name: NameSchema,
  kind: SectionKindSchema,
  patternId: IdSchema,
  bars: z.number().int().min(1).max(MAX_SECTION_BARS),
});
export type Section = z.infer<typeof SectionSchema>;

export const ArrangementSchema = z.object({
  sections: z.array(SectionSchema).max(MAX_SECTIONS),
});
export type Arrangement = z.infer<typeof ArrangementSchema>;

export const ScaleSchema = z.enum(['minor', 'major', 'phrygian', 'dorian', 'harmonicMinor']);
export const KeySchema = z.object({ root: z.number().int().min(0).max(11), scale: ScaleSchema });
export type MusicalKey = z.infer<typeof KeySchema>;

export const GenreSchema = z.enum(['brazilian-phonk', 'drift-phonk', 'dark-phonk', 'trap', 'lofi']);
export type Genre = z.infer<typeof GenreSchema>;
export const MoodSchema = z.enum(['dark', 'aggressive', 'chill', 'melancholic', 'energetic']);
export type Mood = z.infer<typeof MoodSchema>;
export const FocusLevelSchema = z.enum(['subtle', 'normal', 'heavy']);
export type FocusLevel = z.infer<typeof FocusLevelSchema>;

export const GENERATOR_DURATION_MIN = 8;
export const GENERATOR_DURATION_MAX = 300;

/** Fully specified, reproducible input for the procedural composer. */
export const GeneratorParamsSchema = z.object({
  genre: GenreSchema,
  bpm: BpmSchema,
  key: KeySchema,
  mood: MoodSchema,
  intensity: z.number().min(0).max(1),
  complexity: z.number().min(0).max(1),
  durationSec: z.number().int().min(GENERATOR_DURATION_MIN).max(GENERATOR_DURATION_MAX),
  seed: z.number().int().min(0).max(2 ** 31 - 1),
  focus: z.partialRecord(InstrumentSchema, FocusLevelSchema),
});
export type GeneratorParams = z.infer<typeof GeneratorParamsSchema>;

export const TransportSettingsSchema = z.object({
  /** 'pattern' loops the active pattern; 'song' plays the arrangement. */
  mode: z.enum(['pattern', 'song']),
  loopSong: z.boolean(),
});
export type TransportSettings = z.infer<typeof TransportSettingsSchema>;

// ---------------------------------------------------------------- Animation

export const ShapeSchema = z.enum(['circle', 'square', 'triangle']);
export type Shape = z.infer<typeof ShapeSchema>;
export const MotionSchema = z.enum(['orbit', 'pulse', 'bounce', 'spin']);
export type Motion = z.infer<typeof MotionSchema>;

export const SceneElementSchema = z.object({
  id: IdSchema,
  shape: ShapeSchema,
  color: HexColorSchema,
  /** Normalised position (0–1) relative to the stage so layouts survive resizes. */
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  /** Size relative to the smaller stage dimension. */
  size: z.number().min(0.01).max(0.5),
  motion: MotionSchema,
  phase: z.number().min(0).max(1),
});
export type SceneElement = z.infer<typeof SceneElementSchema>;

// ---------------------------------------------------------------- Scene editor (Phase 3A)

export const SCENE_MIN_SIZE = 64;
export const SCENE_MAX_SIZE = 4096;
export const MAX_SCENES = 32;
export const MAX_LAYERS = 32;
export const MAX_SCENE_OBJECTS = 500;
export const MAX_CHARACTERS = 64;
export const MAX_ACCESSORIES = 6;
export const MAX_TEXT_LENGTH = 500;
export const MAX_POLYGON_POINTS = 64;
/** Object coordinates may extend beyond the canvas, but only this far. */
export const SCENE_COORD_LIMIT = 20000;
export const OBJECT_MAX_SIZE = 8192;

const Coord = z.number().min(-SCENE_COORD_LIMIT).max(SCENE_COORD_LIMIT);
const Size = z.number().min(1).max(OBJECT_MAX_SIZE);
const Unit = z.number().min(0).max(1);

// Character poses (Phase 3B). Angles are degrees, clockwise on screen, around stable
// joints: legs at the hips, the torso at the hip centre, the head at the neck and arms
// at the shoulders (head and arms follow the torso). Offsets move the torso or head by a
// fraction of the instance's height. See scene/characters.ts (characterRig).
export const POSE_ANGLE_LIMIT = 180;
export const POSE_OFFSET_LIMIT = 0.25;
export const POSE_ANGLE_KEYS = ['head', 'torso', 'armL', 'armR', 'legL', 'legR'] as const;
export const POSE_OFFSET_KEYS = ['headX', 'headY', 'torsoX', 'torsoY'] as const;
export type PoseKey = (typeof POSE_ANGLE_KEYS)[number] | (typeof POSE_OFFSET_KEYS)[number];
export const POSE_KEYS: readonly PoseKey[] = [...POSE_ANGLE_KEYS, ...POSE_OFFSET_KEYS];
const PoseAngle = z.number().min(-POSE_ANGLE_LIMIT).max(POSE_ANGLE_LIMIT);
const PoseOffset = z.number().min(-POSE_OFFSET_LIMIT).max(POSE_OFFSET_LIMIT);
export const CharacterPoseSchema = z.object({
  head: PoseAngle,
  torso: PoseAngle,
  armL: PoseAngle,
  armR: PoseAngle,
  legL: PoseAngle,
  legR: PoseAngle,
  headX: PoseOffset,
  headY: PoseOffset,
  torsoX: PoseOffset,
  torsoY: PoseOffset,
});
export type CharacterPose = z.infer<typeof CharacterPoseSchema>;
export const NEUTRAL_POSE: CharacterPose = Object.freeze({ head: 0, torso: 0, armL: 0, armR: 0, legL: 0, legR: 0, headX: 0, headY: 0, torsoX: 0, torsoY: 0 });

// ---------------------------------------------------------------- Look v1 (Phase 5B.1)
// Every Look field is optional: absent means exactly the rendering of earlier versions.

/** Layer compositing modes (built into Pixi; no filter pass). */
export const BLEND_MODES = ['normal', 'add', 'screen', 'multiply'] as const;
export const BlendModeSchema = z.enum(BLEND_MODES);
export type BlendMode = z.infer<typeof BlendModeSchema>;

export const MAX_GRADIENT_STOPS = 4;
/** Objects with a glow or a shadow per scene (each costs at least one blurred render pass). */
export const MAX_EFFECT_OBJECTS = 32;
export const EFFECT_RADIUS_MAX = 64;

export const GradientStopSchema = z.object({
  /** Position along the gradient, 0–1. */
  offset: z.number().min(0).max(1),
  color: HexColorSchema,
  opacity: z.number().min(0).max(1),
});
export type GradientStop = z.infer<typeof GradientStopSchema>;

const GradientStops = z
  .array(GradientStopSchema)
  .min(2)
  .max(MAX_GRADIENT_STOPS)
  .refine((stops) => stops.every((s, i) => i === 0 || s.offset >= stops[i - 1]!.offset), 'Gradient stops must be in increasing order of offset');

/**
 * A shape fill gradient, in the shape's own box (so it follows resizing and rotation).
 * Linear: `angle` in degrees, 0 = left → right, 90 = top → bottom. Radial: centre and
 * radius as fractions of the box (radius 0.5 reaches the middle of each edge).
 */
export const FillGradientSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('linear'), stops: GradientStops, angle: z.number().min(0).max(360) }),
  z.object({ type: z.literal('radial'), stops: GradientStops, cx: z.number().min(0).max(1), cy: z.number().min(0).max(1), radius: z.number().min(0.05).max(1.5) }),
]);
export type FillGradient = z.infer<typeof FillGradientSchema>;

/** A soft halo in the object's silhouette, added (lighter) behind it. Radius in scene pixels. */
export const GlowSchema = z.object({
  color: HexColorSchema,
  radius: z.number().min(1).max(EFFECT_RADIUS_MAX),
  strength: z.number().min(0).max(1),
});
export type Glow = z.infer<typeof GlowSchema>;

/** A drop shadow of the object's silhouette. Blur and offsets in scene pixels. */
export const ShadowSchema = z.object({
  color: HexColorSchema,
  blur: z.number().min(0).max(EFFECT_RADIUS_MAX),
  opacity: z.number().min(0).max(1),
  offsetX: z.number().min(-200).max(200),
  offsetY: z.number().min(-200).max(200),
});
export type Shadow = z.infer<typeof ShadowSchema>;

/** Scene colour grade: 1 is neutral for brightness, contrast and saturation; tintAmount 0 is none. */
export const GradeSchema = z.object({
  brightness: z.number().min(0).max(2),
  contrast: z.number().min(0).max(2),
  saturation: z.number().min(0).max(2),
  tint: HexColorSchema,
  tintAmount: z.number().min(0).max(1),
});
export type Grade = z.infer<typeof GradeSchema>;

/** Darkened edges: `amount` is the opacity at the corners, `softness` how far inwards it fades. */
export const VignetteSchema = z.object({
  amount: z.number().min(0).max(1),
  softness: z.number().min(0).max(1),
  color: HexColorSchema,
});
export type Vignette = z.infer<typeof VignetteSchema>;

export const SceneLookSchema = z.object({
  grade: GradeSchema.nullable().optional(),
  vignette: VignetteSchema.nullable().optional(),
});
export type SceneLook = z.infer<typeof SceneLookSchema>;

export const LayerSchema = z.object({
  id: IdSchema,
  name: z.string().trim().min(1).max(60),
  visible: z.boolean(),
  locked: z.boolean(),
  /** Phase 5B.1: multiplies the opacity of everything on the layer (absent = 1). */
  opacity: z.number().min(0).max(1).optional(),
  /** Phase 5B.1: how the layer combines with what is below it (absent = normal). */
  blend: BlendModeSchema.optional(),
});
export type Layer = z.infer<typeof LayerSchema>;

export const StrokeSchema = z.object({
  color: HexColorSchema,
  width: z.number().min(0).max(64),
});

const ObjectBase = {
  id: IdSchema,
  name: z.string().trim().min(1).max(60),
  layerId: IdSchema,
  /** Top-left of the object's box in scene pixels (before rotation about the anchor). */
  x: Coord,
  y: Coord,
  width: Size,
  height: Size,
  /** Degrees, clockwise, around the anchor point. */
  rotation: z.number().min(-360).max(360),
  opacity: Unit,
  /** Rotation/scale origin as a fraction of the box (0.5, 0.5 = centre). */
  anchorX: Unit,
  anchorY: Unit,
  visible: z.boolean(),
  locked: z.boolean(),
  /** Objects sharing a group id are selected and moved together. */
  groupId: IdSchema.nullable(),
  /** Phase 5B.1 (optional): a glow and a drop shadow behind the object. */
  glow: GlowSchema.nullable().optional(),
  shadow: ShadowSchema.nullable().optional(),
};

export const ShapeStyleSchema = z.object({
  /** Solid fill; also the fallback colour (thumbnails, older builds) when a gradient is set. */
  fill: HexColorSchema,
  stroke: StrokeSchema.nullable(),
  /** Phase 5B.1 (optional): a gradient fill that replaces the solid fill when drawing. */
  gradient: FillGradientSchema.nullable().optional(),
});

export const SceneObjectSchema = z.discriminatedUnion('type', [
  z.object({ ...ObjectBase, type: z.literal('rect'), style: ShapeStyleSchema, cornerRadius: z.number().min(0).max(1024) }),
  z.object({ ...ObjectBase, type: z.literal('circle'), style: ShapeStyleSchema }),
  z.object({ ...ObjectBase, type: z.literal('ellipse'), style: ShapeStyleSchema }),
  z.object({
    ...ObjectBase,
    type: z.literal('polygon'),
    style: ShapeStyleSchema,
    /** Vertices as fractions of the object's box. */
    points: z.array(z.tuple([Unit, Unit])).min(3).max(MAX_POLYGON_POINTS),
  }),
  z.object({
    ...ObjectBase,
    type: z.literal('text'),
    text: z.string().min(1).max(MAX_TEXT_LENGTH),
    fontSize: z.number().min(6).max(512),
    fontFamily: z.enum(['sans', 'serif', 'mono']),
    bold: z.boolean(),
    align: z.enum(['left', 'center', 'right']),
    color: HexColorSchema,
  }),
  z.object({ ...ObjectBase, type: z.literal('image'), assetId: IdSchema, fit: z.enum(['stretch', 'contain', 'cover']) }),
  z.object({
    ...ObjectBase,
    type: z.literal('character'),
    characterId: IdSchema,
    flipX: z.boolean(),
    /** Base pose of this instance (Phase 3B); absent means the neutral pose. */
    pose: CharacterPoseSchema.optional(),
  }),
]);
export type SceneObject = z.infer<typeof SceneObjectSchema>;
export type SceneObjectType = SceneObject['type'];
export const SCENE_OBJECT_TYPES: readonly SceneObjectType[] = ['rect', 'circle', 'ellipse', 'polygon', 'text', 'image', 'character'];

export const GradientSchema = z.object({
  to: HexColorSchema,
  /** Direction in degrees: 90 = top → bottom. */
  angle: z.number().min(0).max(360),
});

export const BackgroundImageSchema = z.object({
  assetId: IdSchema,
  fit: z.enum(['cover', 'contain', 'stretch', 'center']),
  /** Extra zoom applied after fitting. */
  scale: z.number().min(0.1).max(10),
  /** Offset in scene pixels. */
  offsetX: Coord,
  offsetY: Coord,
  opacity: Unit,
});

export const SceneSchema = z
  .object({
    id: IdSchema,
    name: NameSchema,
    /** Background colour (the gradient's start colour when a gradient is set). */
    background: HexColorSchema,
    /** Phase 0 animation speed for the animated shapes. */
    speed: z.number().min(0.1).max(5),
    /** Phase 0 animated shapes; drawn above the edited scene. */
    elements: z.array(SceneElementSchema).max(64),
    width: z.number().int().min(SCENE_MIN_SIZE).max(SCENE_MAX_SIZE),
    height: z.number().int().min(SCENE_MIN_SIZE).max(SCENE_MAX_SIZE),
    gradient: GradientSchema.nullable(),
    backgroundImage: BackgroundImageSchema.nullable(),
    /** Bottom → top. */
    layers: z.array(LayerSchema).min(1).max(MAX_LAYERS),
    /** Draw order within a layer follows array order. */
    objects: z.array(SceneObjectSchema).max(MAX_SCENE_OBJECTS),
    /** Phase 5B.1 (optional): colour grade and vignette over the whole scene. */
    look: SceneLookSchema.nullable().optional(),
  })
  .superRefine((scene, ctx) => {
    checkUnique(ctx, scene.layers, (l) => l.id, 'layers', 'layer id');
    checkUnique(ctx, scene.objects, (o) => o.id, 'objects', 'object id');
    const layers = new Set(scene.layers.map((l) => l.id));
    scene.objects.forEach((o, i) => {
      if (!layers.has(o.layerId)) ctx.addIssue({ code: 'custom', path: ['objects', i, 'layerId'], message: `Object "${o.name}" is on a missing layer` });
    });
    const effects = scene.objects.filter((o) => o.glow || o.shadow).length;
    if (effects > MAX_EFFECT_OBJECTS) {
      ctx.addIssue({ code: 'custom', path: ['objects'], message: `At most ${MAX_EFFECT_OBJECTS} objects per scene can have a glow or shadow (this scene would have ${effects})` });
    }
  });
export type Scene = z.infer<typeof SceneSchema>;

// ---------------------------------------------------------------- Characters (Phase 3A)

export const CHARACTER_STYLES = {
  head: ['round', 'oval', 'square'],
  eyes: ['dot', 'round', 'sleepy', 'visor'],
  hair: ['none', 'short', 'spiky', 'long', 'bun', 'mohawk'],
  top: ['tshirt', 'hoodie', 'robe', 'armor', 'suit'],
  bottom: ['pants', 'shorts', 'skirt', 'robe'],
  accessory: ['hat', 'hood', 'glasses', 'cape', 'backpack', 'sword', 'antenna', 'scarf', 'headphones', 'belt'],
} as const;

export const CharacterProportionsSchema = z.object({
  /** Head diameter relative to the character's height. */
  headSize: z.number().min(0.12).max(0.32),
  /** Torso width relative to the character's width. */
  torsoWidth: z.number().min(0.3).max(0.7),
  /** Torso height relative to the character's height. */
  torsoHeight: z.number().min(0.2).max(0.4),
  /** Leg length relative to the character's height. */
  legLength: z.number().min(0.2).max(0.45),
  /** Arm length relative to the torso height. */
  armLength: z.number().min(0.6).max(1.3),
  /** Limb thickness relative to the character's width. */
  limbThickness: z.number().min(0.06).max(0.16),
});
export type CharacterProportions = z.infer<typeof CharacterProportionsSchema>;

export const AccessorySchema = z.object({
  kind: z.enum(CHARACTER_STYLES.accessory),
  color: HexColorSchema,
});
export type Accessory = z.infer<typeof AccessorySchema>;

export const CharacterSchema = z.object({
  id: IdSchema,
  name: z.string().trim().min(1).max(60),
  proportions: CharacterProportionsSchema,
  skin: HexColorSchema,
  head: z.object({ shape: z.enum(CHARACTER_STYLES.head) }),
  eyes: z.object({ style: z.enum(CHARACTER_STYLES.eyes), color: HexColorSchema }),
  hair: z.object({ style: z.enum(CHARACTER_STYLES.hair), color: HexColorSchema }),
  clothing: z.object({
    top: z.enum(CHARACTER_STYLES.top),
    topColor: HexColorSchema,
    accentColor: HexColorSchema,
    bottom: z.enum(CHARACTER_STYLES.bottom),
    bottomColor: HexColorSchema,
    shoeColor: HexColorSchema,
  }),
  accessories: z.array(AccessorySchema).max(MAX_ACCESSORIES),
});
export type Character = z.infer<typeof CharacterSchema>;

// ---------------------------------------------------------------- Keyframe animation (Phase 3B)

/**
 * Integer frame indices are the canonical timebase: frames run 0 … durationFrames − 1 at
 * `fps`; seconds are derived (frame / fps) for display and playback only. A keyframe's
 * `easing` shapes the segment from it to the next keyframe on the same track.
 */
export const ANIMATION_FPS = [12, 24, 30, 60] as const;
export type AnimationFps = (typeof ANIMATION_FPS)[number];
export const MAX_ANIMATION_SECONDS = 120;
export const MAX_ANIMATIONS = 64;
export const MAX_ANIMATIONS_PER_SCENE = 16;
export const MAX_TRACKS = 256;
export const MAX_KEYFRAMES_PER_TRACK = 1000;
export const MAX_KEYFRAMES_PER_ANIMATION = 5000;
export const MAX_ROTATION_VALUE = 36000;
export const MAX_SCALE_VALUE = 20;

export const TRANSFORM_CHANNELS = ['x', 'y', 'rotation', 'scaleX', 'scaleY', 'opacity', 'visible'] as const;
export const POSE_CHANNELS = POSE_KEYS.map((k) => `pose.${k}` as const);
export const ChannelSchema = z.enum([...TRANSFORM_CHANNELS, ...(POSE_CHANNELS as `pose.${PoseKey}`[])]);
export type Channel = z.infer<typeof ChannelSchema>;
export const isPoseChannel = (c: Channel): c is `pose.${PoseKey}` => c.startsWith('pose.');

/** Valid value range of each channel (also used to clamp edits). */
export function channelRange(channel: Channel): [number, number] {
  switch (channel) {
    case 'x':
    case 'y':
      return [-SCENE_COORD_LIMIT, SCENE_COORD_LIMIT];
    case 'rotation':
      return [-MAX_ROTATION_VALUE, MAX_ROTATION_VALUE];
    case 'scaleX':
    case 'scaleY':
      return [0, MAX_SCALE_VALUE];
    case 'opacity':
    case 'visible':
      return [0, 1];
    default:
      return (POSE_OFFSET_KEYS as readonly string[]).includes(channel.slice(5)) ? [-POSE_OFFSET_LIMIT, POSE_OFFSET_LIMIT] : [-POSE_ANGLE_LIMIT, POSE_ANGLE_LIMIT];
  }
}

export const EasingSchema = z.enum(['step', 'linear', 'ease-in', 'ease-out', 'ease-in-out']);
export type Easing = z.infer<typeof EasingSchema>;
export const LoopModeSchema = z.enum(['once', 'loop', 'pingpong']);
export type LoopMode = z.infer<typeof LoopModeSchema>;

export const KeyframeSchema = z.object({
  frame: z.number().int().min(0),
  value: z.number().min(-MAX_ROTATION_VALUE).max(MAX_ROTATION_VALUE),
  easing: EasingSchema,
});
export type Keyframe = z.infer<typeof KeyframeSchema>;

export const AnimationTrackSchema = z
  .object({
    id: IdSchema,
    /** Stable id of the animated object in the animation's scene. */
    objectId: IdSchema,
    channel: ChannelSchema,
    /** Sorted by frame; at most one keyframe per frame. */
    keyframes: z.array(KeyframeSchema).max(MAX_KEYFRAMES_PER_TRACK),
  })
  .superRefine((track, ctx) => {
    const [lo, hi] = channelRange(track.channel);
    track.keyframes.forEach((k, i) => {
      const previous = track.keyframes[i - 1];
      if (previous && k.frame === previous.frame) ctx.addIssue({ code: 'custom', path: ['keyframes', i, 'frame'], message: `Duplicate keyframe at frame ${k.frame} on ${track.channel}` });
      else if (previous && k.frame < previous.frame) ctx.addIssue({ code: 'custom', path: ['keyframes', i, 'frame'], message: `Keyframes on ${track.channel} are out of order` });
      if (k.value < lo || k.value > hi) ctx.addIssue({ code: 'custom', path: ['keyframes', i, 'value'], message: `${track.channel} must be between ${lo} and ${hi}` });
      if (track.channel === 'visible' && ((k.value !== 0 && k.value !== 1) || k.easing !== 'step')) {
        ctx.addIssue({ code: 'custom', path: ['keyframes', i], message: 'Visibility keyframes are 0 or 1 and always step' });
      }
    });
  });
export type AnimationTrack = z.infer<typeof AnimationTrackSchema>;

export const AnimationSchema = z
  .object({
    id: IdSchema,
    name: z.string().trim().min(1).max(60),
    sceneId: IdSchema,
    fps: z.union(ANIMATION_FPS.map((f) => z.literal(f)) as [z.ZodLiteral<12>, z.ZodLiteral<24>, z.ZodLiteral<30>, z.ZodLiteral<60>]),
    durationFrames: z.number().int().min(1).max(MAX_ANIMATION_SECONDS * 60),
    loop: LoopModeSchema,
    tracks: z.array(AnimationTrackSchema).max(MAX_TRACKS),
  })
  .superRefine((a, ctx) => {
    if (a.durationFrames > MAX_ANIMATION_SECONDS * a.fps) {
      ctx.addIssue({ code: 'custom', path: ['durationFrames'], message: `An animation can last at most ${MAX_ANIMATION_SECONDS} seconds` });
    }
    checkUnique(ctx, a.tracks, (t) => t.id, 'tracks', 'track id');
    checkUnique(ctx, a.tracks, (t) => `${t.objectId}/${t.channel}`, 'tracks', 'track for object/channel');
    let keys = 0;
    a.tracks.forEach((t, ti) => {
      keys += t.keyframes.length;
      t.keyframes.forEach((k, ki) => {
        if (k.frame >= a.durationFrames) ctx.addIssue({ code: 'custom', path: ['tracks', ti, 'keyframes', ki, 'frame'], message: `Keyframe at frame ${k.frame} is past the end (frame ${a.durationFrames - 1})` });
      });
    });
    if (keys > MAX_KEYFRAMES_PER_ANIMATION) ctx.addIssue({ code: 'custom', path: ['tracks'], message: `An animation can hold at most ${MAX_KEYFRAMES_PER_ANIMATION} keyframes` });
  });
export type Animation = z.infer<typeof AnimationSchema>;

// ---------------------------------------------------------------- Audio-visual sessions (Phase 4A)

/**
 * An audio-visual session links a music source to a scene and (optionally) one of its
 * animations, and holds the reactions (mappings) and markers that synchronise them. It
 * stores only references and settings: musical events are re-extracted from the
 * arrangement, keyframes stay in their animation, and audio stays in the asset store.
 * Every musical position is in ticks (PPQ 192), the exact unit of the music compiler.
 */
export const AV_TICKS_PER_BEAT = 192;
export const AV_MAX_TICK = MAX_SONG_BARS * STEPS_PER_BAR * (AV_TICKS_PER_BEAT / 4);
export const MAX_AV_SESSIONS = 8;
export const MAX_AV_MAPPINGS = 32;
export const MAX_AV_MOTION_CUES = 32;
export const MAX_AV_MARKERS = 256;

/** Phase 3C motion templates (motion cues compile them into keyframes). */
export const MOTION_TEMPLATE_IDS = [
  'idle',
  'walk',
  'run',
  'jump',
  'land',
  'crouch',
  'wave',
  'turn',
  'celebrate',
  'fall',
  'float',
  'bounce',
  'spin',
  'shake',
  'pulse',
  'fadeIn',
  'fadeOut',
  'slide',
  'sway',
  'path',
] as const;

export const NOTE_EVENT_CATEGORIES = ['kick', 'snare', 'clap', 'hats', 'perc', 'cowbell', 'bass808', 'lead', 'pad'] as const;
export const EVENT_CATEGORIES = [...NOTE_EVENT_CATEGORIES, 'beat', 'bar', 'section', 'clip', 'manual'] as const;
export const EventCategorySchema = z.enum(EVENT_CATEGORIES);
export type EventCategory = z.infer<typeof EventCategorySchema>;

/** Reactions on one object, or on the whole scene (flash, background, shake). */
export const OBJECT_REACTIONS = ['opacity', 'scale', 'position', 'rotation', 'tint', 'pose', 'visibility'] as const;
export const SCENE_REACTIONS = ['flash', 'background', 'shake'] as const;
export const ReactionTypeSchema = z.enum([...OBJECT_REACTIONS, ...SCENE_REACTIONS]);
export type ReactionType = z.infer<typeof ReactionTypeSchema>;
export const POSE_REACTION_PRESETS = ['arms-up', 'crouch', 'lean', 'head-bob', 'kick-out'] as const;

const AvTick = z.number().int().min(0).max(AV_MAX_TICK);

export const EnvelopeSchema = z.object({
  /** instant: jump up, decay · smooth: eased rise and fall · impulse: very short spike · hold: sustain then release. */
  shape: z.enum(['instant', 'smooth', 'impulse', 'hold']),
  attack: z.number().min(0).max(2),
  hold: z.number().min(0).max(8),
  decay: z.number().min(0).max(8),
  /** Ease the recovery (ease-out) instead of a linear return. */
  easedRecovery: z.boolean(),
});
export type Envelope = z.infer<typeof EnvelopeSchema>;

export const ReactionParamsSchema = z.object({
  /**
   * Size of the reaction at full envelope, in the reaction's unit: opacity Δ (−1…1),
   * scale Δ (0.15 = ×1.15), position px, rotation degrees, tint/flash/background
   * strength (0…1), pose strength (×), shake px. Clamped again when composed.
   */
  amount: z.number().min(-360).max(2000),
  direction: z.enum(['up', 'down', 'left', 'right']).optional(),
  color: HexColorSchema.optional(),
  pose: z.enum(POSE_REACTION_PRESETS).optional(),
  /** Visibility: toggle on every trigger, or show/hide while the envelope is active. */
  mode: z.enum(['toggle', 'show', 'hide']).optional(),
});

export const AvMappingSchema = z.object({
  id: IdSchema,
  name: z.string().trim().min(1).max(60),
  enabled: z.boolean(),
  source: z.object({
    categories: z.array(EventCategorySchema).min(1).max(EVENT_CATEGORIES.length),
    /** Use every Nth matching event (1 = all), starting at `phase`. */
    every: z.number().int().min(1).max(256),
    phase: z.number().int().min(0).max(255),
    minVelocity: z.number().min(0).max(1),
    /** Only events in this arrangement section (null = anywhere). */
    sectionId: IdSchema.nullable(),
  }),
  target: z.discriminatedUnion('kind', [z.object({ kind: z.literal('object'), objectId: IdSchema }), z.object({ kind: z.literal('scene') })]),
  reaction: ReactionTypeSchema,
  params: ReactionParamsSchema,
  envelope: EnvelopeSchema,
  /** Shift every trigger by this many beats (negative = anticipate). */
  offsetBeats: z.number().min(-16).max(16),
  /** Snap triggers to a beat subdivision (0 = no snapping; 1 beats, 2 eighths, 3 triplets, 4 sixteenths). */
  quantize: z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
  /** Ignore triggers closer than this to the previous accepted one. */
  cooldownBeats: z.number().min(0).max(64),
  /** 0 = every trigger at full size; 1 = scaled by note velocity. */
  velocitySensitivity: z.number().min(0).max(1),
  /** Seed for the deterministic parts of a reaction (shake direction). */
  seed: z.number().int().min(0).max(2 ** 31 - 1),
});
export type AvMapping = z.infer<typeof AvMappingSchema>;

export const AvMarkerSchema = z.object({
  id: IdSchema,
  tick: AvTick,
  label: z.string().trim().max(40),
});
export type AvMarker = z.infer<typeof AvMarkerSchema>;

/** A Phase 3C motion placed on the musical timeline: played as a non-destructive overlay; baked into keyframes on request. */
export const AvMotionCueSchema = z.object({
  id: IdSchema,
  template: z.enum(MOTION_TEMPLATE_IDS),
  objectId: IdSchema,
  tick: AvTick,
  /** Repeat every this many ticks (null = once), `count` times. */
  everyTicks: AvTick.nullable(),
  count: z.number().int().min(1).max(64),
  /** Beats (null = the template's default duration). */
  durationBeats: z.number().positive().max(256).nullable(),
  params: z.record(z.string().max(32), z.union([z.number(), z.string().max(40), z.boolean()])),
});
export type AvMotionCue = z.infer<typeof AvMotionCueSchema>;

export const AvMusicSourceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('song') }),
  z.object({ kind: z.literal('pattern'), patternId: IdSchema, bars: z.number().int().min(1).max(64) }),
]);
export type AvMusicSource = z.infer<typeof AvMusicSourceSchema>;

// ---------------------------------------------------------------- Visualizers (Phase 4C)

/**
 * Music visualizer layers stored on an audio-visual session (schema v7). Every value is
 * bounded so no setting can create an unbounded CPU or GPU workload. See
 * docs/VISUALIZER_STUDIO.md.
 */
export const VISUALIZER_TYPES = ['bars', 'radial', 'waveform', 'pulse', 'particles', 'grid'] as const;
export type VisualizerType = (typeof VISUALIZER_TYPES)[number];
export const VISUALIZER_BLENDS = ['normal', 'add', 'screen'] as const;
export const VISUALIZER_PLACEMENTS = ['behind', 'front'] as const;
export const AUDIO_BANDS = ['full', 'low', 'mid', 'high'] as const;
export type AudioBand = (typeof AUDIO_BANDS)[number];
export const MAX_VISUALIZER_LAYERS = 8;
/** Live particles across all layers of one composition. */
export const MAX_COMPOSITION_PARTICLES = 1200;
/** Strongest allowed screen flash (photosensitivity). */
export const MAX_VISUALIZER_FLASH = 0.6;

const VizCoord = z.number().min(-4000).max(8000);

export const VisualizerResponseSchema = z.object({
  /** measured: analysed audio features · events: musical events (synthetic, labelled "Event-driven"). */
  source: z.enum(['measured', 'events']),
  /** Measured scalar band (bars and radial use the whole spectrum). */
  band: z.enum(AUDIO_BANDS),
  /** Events that trigger or drive the layer. */
  categories: z.array(EventCategorySchema).min(1).max(EVENT_CATEGORIES.length),
  /** Response gain. */
  sensitivity: z.number().min(0).max(4),
  /** 0 = raw … 1 = heavily averaged (up to 10 analysis hops). */
  smoothing: z.number().min(0).max(1),
  /** Release time in seconds (peak hold with exponential fall). */
  decay: z.number().min(0).max(2),
});
export type VisualizerResponse = z.infer<typeof VisualizerResponseSchema>;

export const VisualizerStyleSchema = z.object({
  primary: HexColorSchema,
  secondary: HexColorSchema,
  /** Soft underlay glow strength (0–1). */
  glow: z.number().min(0).max(1),
});

const vizBase = {
  id: IdSchema,
  name: z.string().trim().min(1).max(40),
  visible: z.boolean(),
  placement: z.enum(VISUALIZER_PLACEMENTS),
  blend: z.enum(VISUALIZER_BLENDS),
  /** Centre of the layer box, scene coordinates. */
  x: VizCoord,
  y: VizCoord,
  width: z.number().min(10).max(4000),
  height: z.number().min(10).max(4000),
  scale: z.number().min(0.1).max(4),
  rotation: z.number().min(-360).max(360),
  opacity: z.number().min(0).max(1),
  style: VisualizerStyleSchema,
  response: VisualizerResponseSchema,
  seed: z.number().int().min(0).max(2 ** 31 - 1),
};

export const BarsParamsSchema = z.object({
  orientation: z.enum(['horizontal', 'vertical']),
  count: z.number().int().min(4).max(128),
  /** Gap as a fraction of each bar slot. */
  gap: z.number().min(0).max(0.9),
  mirrored: z.boolean(),
  /** Resting height as a fraction of the box. */
  minHeight: z.number().min(0).max(1),
});
export const RadialParamsSchema = z.object({
  count: z.number().int().min(8).max(180),
  /** Inner radius as a fraction of half the box. */
  innerRadius: z.number().min(0).max(0.95),
  /** Maximum bar length as a fraction of half the box. */
  length: z.number().min(0.05).max(1),
  direction: z.enum(['outward', 'inward', 'both']),
  /** Turns per bar of music (tempo-synced rotation). */
  spin: z.number().min(-2).max(2),
  symmetry: z.number().int().min(1).max(8),
  barWidth: z.number().min(0.1).max(1),
});
export const WaveformParamsSchema = z.object({
  thickness: z.number().min(1).max(24),
  filled: z.boolean(),
  mirrored: z.boolean(),
  /** Length of audio shown across the box. */
  windowMs: z.number().min(10).max(500),
  points: z.number().int().min(32).max(512),
});
export const PULSE_SHAPES = ['ring', 'square', 'triangle', 'hexagon'] as const;
export const PulseParamsSchema = z.object({
  shape: z.enum(PULSE_SHAPES),
  maxRings: z.number().int().min(1).max(12),
  /** Final ring size as a fraction of the box. */
  expand: z.number().min(0.1).max(2),
  lineWidth: z.number().min(1).max(30),
  /** Screen flash strength on each trigger (0 = off). */
  flash: z.number().min(0).max(MAX_VISUALIZER_FLASH),
  /** Size of the always-visible centre shape (0 = none). */
  core: z.number().min(0).max(1),
});
export const ParticlesParamsSchema = z.object({
  /** Most particles alive at once (oldest are dropped first). */
  count: z.number().int().min(10).max(600),
  perEvent: z.number().int().min(0).max(64),
  /** Continuous emission per second (gated by the level when measured). */
  rate: z.number().min(0).max(120),
  lifetime: z.number().min(0.2).max(6),
  speed: z.number().min(0).max(800),
  /** Emission cone in degrees around `direction`. */
  spread: z.number().min(0).max(360),
  /** Degrees; 0 = right, −90 = up. */
  direction: z.number().min(-180).max(180),
  gravity: z.number().min(-1000).max(1000),
  size: z.number().min(1).max(20),
});
export const GridParamsSchema = z.object({
  lines: z.number().int().min(4).max(40),
  /** Grid rows scrolled per beat. */
  speed: z.number().min(0).max(4),
  /** Horizon height as a fraction of the box (0 = top). */
  horizon: z.number().min(0).max(0.9),
  /** Brightness lift on beats (0–1). */
  pulse: z.number().min(0).max(1),
});

export const VisualizerLayerSchema = z.discriminatedUnion('type', [
  z.object({ ...vizBase, type: z.literal('bars'), params: BarsParamsSchema }),
  z.object({ ...vizBase, type: z.literal('radial'), params: RadialParamsSchema }),
  z.object({ ...vizBase, type: z.literal('waveform'), params: WaveformParamsSchema }),
  z.object({ ...vizBase, type: z.literal('pulse'), params: PulseParamsSchema }),
  z.object({ ...vizBase, type: z.literal('particles'), params: ParticlesParamsSchema }),
  z.object({ ...vizBase, type: z.literal('grid'), params: GridParamsSchema }),
]);
export type VisualizerLayer = z.infer<typeof VisualizerLayerSchema>;

export const VisualizerSchema = z
  .object({ layers: z.array(VisualizerLayerSchema).max(MAX_VISUALIZER_LAYERS) })
  .superRefine((v, ctx) => {
    checkUnique(ctx, v.layers, (l) => l.id, 'layers', 'layer id');
    const particles = v.layers.reduce((n, l) => n + (l.type === 'particles' ? l.params.count : 0), 0);
    if (particles > MAX_COMPOSITION_PARTICLES) {
      ctx.addIssue({ code: 'custom', path: ['layers'], message: `The layers allow ${particles} particles; the limit is ${MAX_COMPOSITION_PARTICLES} per composition` });
    }
  });
export type Visualizer = z.infer<typeof VisualizerSchema>;

export const AvSessionSchema = z
  .object({
    id: IdSchema,
    name: z.string().trim().min(1).max(60),
    version: z.literal(1),
    music: AvMusicSourceSchema,
    sceneId: IdSchema,
    /** The scene's animation played in sync (null = base scene with reactions only). */
    animationId: IdSchema.nullable(),
    /** Where the animation's frame 0 sits on the musical timeline. */
    animationStartTick: AvTick,
    loop: z.object({ enabled: z.boolean(), startTick: AvTick, endTick: AvTick }),
    /** At the end of the session: stop, or loop the whole session. */
    endBehavior: z.enum(['stop', 'loop']),
    /** music: the music's length · animation: the animation's · custom: customSeconds. */
    durationMode: z.enum(['music', 'animation', 'custom']),
    customSeconds: z.number().positive().max(600).nullable(),
    /** Play without audio (the visual-only timebase). */
    muted: z.boolean(),
    markers: z.array(AvMarkerSchema).max(MAX_AV_MARKERS),
    mappings: z.array(AvMappingSchema).max(MAX_AV_MAPPINGS),
    motionCues: z.array(AvMotionCueSchema).max(MAX_AV_MOTION_CUES),
    /** Music visualizer layers (Phase 4C, schema v7). */
    visualizer: VisualizerSchema,
  })
  .superRefine((s, ctx) => {
    if (s.loop.endTick <= s.loop.startTick) ctx.addIssue({ code: 'custom', path: ['loop'], message: 'The loop must end after it starts' });
    if (s.durationMode === 'custom' && s.customSeconds === null) ctx.addIssue({ code: 'custom', path: ['customSeconds'], message: 'A custom duration needs a length' });
    checkUnique(ctx, s.markers, (m) => m.id, 'markers', 'marker id');
    checkUnique(ctx, s.mappings, (m) => m.id, 'mappings', 'mapping id');
    checkUnique(ctx, s.motionCues, (m) => m.id, 'motionCues', 'motion cue id');
    s.mappings.forEach((m, i) => {
      const scene = (SCENE_REACTIONS as readonly string[]).includes(m.reaction);
      if (scene !== (m.target.kind === 'scene')) {
        ctx.addIssue({ code: 'custom', path: ['mappings', i, 'target'], message: scene ? `A ${m.reaction} reaction applies to the whole scene` : `A ${m.reaction} reaction needs an object` });
      }
      if (m.reaction === 'pose' && !m.params.pose) ctx.addIssue({ code: 'custom', path: ['mappings', i, 'params', 'pose'], message: 'Choose a pose' });
      if (m.reaction === 'visibility' && !m.params.mode) ctx.addIssue({ code: 'custom', path: ['mappings', i, 'params', 'mode'], message: 'Choose toggle, show or hide' });
    });
  });
export type AvSession = z.infer<typeof AvSessionSchema>;

// ---------------------------------------------------------------- Assets

export const AssetKindSchema = z.enum(['audio', 'image', 'video', 'pattern', 'other']);
export type AssetKind = z.infer<typeof AssetKindSchema>;

/** Media kinds whose MIME type must belong to the matching top-level type (audio/*, …). */
const MEDIA_KINDS: readonly AssetKind[] = ['audio', 'image', 'video'];
const MIME_PATTERN = /^([a-z0-9][a-z0-9!#$&^_.+-]*)\/[a-z0-9][a-z0-9!#$&^_.+-]*(\s*;.*)?$/i;

/** Lower-cases a MIME type and drops parameters: "Audio/WAV; rate=8000" → "audio/wav". */
export function normalizeMimeType(mimeType: string): string {
  return mimeType.split(';')[0]!.trim().toLowerCase();
}

/** True when `mimeType` is well-formed and consistent with the asset kind. */
export function mimeMatchesKind(kind: AssetKind, mimeType: string): boolean {
  if (!MEDIA_KINDS.includes(kind)) return true;
  const match = MIME_PATTERN.exec(mimeType.trim());
  return match !== null && match[1]!.toLowerCase() === kind;
}

export const AssetAudioInfoSchema = z.object({
  durationSec: z.number().min(0).max(3600),
  channels: z.number().int().min(1).max(32),
  sampleRate: z.number().int().min(3000).max(384000),
  /** Absolute peak (0–1) of the decoded audio, used for normalisation. */
  peak: z.number().min(0).max(1),
});
export type AssetAudioInfo = z.infer<typeof AssetAudioInfoSchema>;

export const AssetImageInfoSchema = z.object({
  width: z.number().int().min(1).max(16384),
  height: z.number().int().min(1).max(16384),
});
export type AssetImageInfo = z.infer<typeof AssetImageInfoSchema>;

export const AssetSchema = z
  .object({
    id: IdSchema,
    name: z.string().trim().min(1).max(200),
    kind: AssetKindSchema,
    mimeType: z.string().max(100),
    sizeBytes: z.number().int().min(0),
    source: z.enum(['local-file', 'recording', 'procedural', 'provider']),
    createdAt: IsoDateSchema,
    /** Filled when the file is first decoded; missing for Phase 0/1 uploads until then. */
    audio: AssetAudioInfoSchema.optional(),
    /** Pixel size of an image asset (read from the file header on import). */
    image: AssetImageInfoSchema.optional(),
  })
  .superRefine((asset, ctx) => {
    if (!mimeMatchesKind(asset.kind, asset.mimeType)) {
      ctx.addIssue({
        code: 'custom',
        path: ['mimeType'],
        message: `MIME type "${asset.mimeType}" does not match asset kind "${asset.kind}"`,
      });
    }
  });
export type Asset = z.infer<typeof AssetSchema>;

/**
 * Assets whose content lives in a stored binary (rather than in the project document).
 * Phase 0: every file the user added locally. Exports must embed these binaries.
 */
export function assetRequiresBinary(asset: Asset): boolean {
  return asset.source === 'local-file' || asset.source === 'recording';
}

// ---------------------------------------------------------------- Generation

export const GenerationKindSchema = z.enum([
  'text-to-music',
  'text-to-audio',
  'text-to-video',
  'speech',
  'procedural',
]);
export type GenerationKind = z.infer<typeof GenerationKindSchema>;

export const GenerationStatusSchema = z.enum(['queued', 'running', 'succeeded', 'failed', 'unavailable', 'cancelled']);
export type GenerationStatus = z.infer<typeof GenerationStatusSchema>;

const ParamValueSchema = z.union([z.string().max(2000), z.number(), z.boolean()]);

export const GenerationRequestSchema = z.object({
  id: IdSchema,
  kind: GenerationKindSchema,
  providerId: IdSchema,
  prompt: z.string().max(4000),
  params: z.record(z.string().max(64), ParamValueSchema),
  status: GenerationStatusSchema,
  createdAt: IsoDateSchema,
  error: z.string().max(2000).optional(),
});
export type GenerationRequest = z.infer<typeof GenerationRequestSchema>;

export const ProviderCapabilitiesSchema = z.object({
  providerId: IdSchema,
  displayName: z.string().min(1).max(120),
  kinds: z.array(GenerationKindSchema).min(1),
  /** True when the provider runs entirely inside the browser with no network access. */
  local: z.boolean(),
  requiresConfiguration: z.boolean(),
  maxPromptLength: z.number().int().positive(),
  outputFormats: z.array(z.string().max(60)),
  supportsCancellation: z.boolean(),
});
export type ProviderCapabilities = z.infer<typeof ProviderCapabilitiesSchema>;

export const GenerationOutputSchema = z.object({
  id: IdSchema,
  requestId: IdSchema,
  kind: GenerationKindSchema,
  providerId: IdSchema,
  createdAt: IsoDateSchema,
  assetId: IdSchema.optional(),
  /** Structured, provider-specific payload (for example a procedural pattern). */
  data: z.record(z.string().max(64), z.unknown()).optional(),
});
export type GenerationOutput = z.infer<typeof GenerationOutputSchema>;

// ---------------------------------------------------------------- Audio clips

export const ClipFilterSchema = z.object({
  type: z.enum(['off', 'lowpass', 'highpass']),
  frequencyHz: z.number().min(20).max(20000),
});

/**
 * An audio clip on the song timeline. Position uses the arrangement's timebase
 * (16th-note steps from the start of the song); everything inside the clip is in
 * seconds of source audio, so tempo changes move clips but never stretch audio.
 */
export const AudioClipSchema = z.object({
  id: IdSchema,
  name: z.string().trim().min(1).max(120),
  assetId: IdSchema,
  lane: z.number().int().min(0).max(AUDIO_LANES - 1),
  startStep: z.number().min(0).max(MAX_SONG_BARS * STEPS_PER_BAR),
  /** Where in the source file playback begins (seconds). */
  offsetSec: z.number().min(0).max(MAX_SAMPLE_SECONDS),
  /** How much source audio one pass plays (seconds, before playback rate). */
  durationSec: z.number().min(0.01).max(MAX_SAMPLE_SECONDS),
  gainDb: z.number().min(-48).max(12),
  muted: z.boolean(),
  fadeInSec: z.number().min(0).max(MAX_FADE_SECONDS),
  fadeOutSec: z.number().min(0).max(MAX_FADE_SECONDS),
  /** Speed and pitch change together (no time-stretching). */
  playbackRate: z.number().min(0.25).max(4),
  reverse: z.boolean(),
  /** Scale the clip so the source's peak reaches −1 dBFS before gainDb. */
  normalize: z.boolean(),
  loops: z.number().int().min(1).max(MAX_CLIP_LOOPS),
  filter: ClipFilterSchema,
});
export type AudioClip = z.infer<typeof AudioClipSchema>;

/** Mixer channel that every audio clip plays through. */
export const AudioBusSchema = z.object({
  volumeDb: z.number().min(-60).max(6),
  pan: z.number().min(-1).max(1),
  muted: z.boolean(),
  solo: z.boolean(),
  delaySend: z.number().min(0).max(1),
  reverbSend: z.number().min(0).max(1),
});
export type AudioBus = z.infer<typeof AudioBusSchema>;

// ---------------------------------------------------------------- Project

export const ProjectSettingsSchema = z.object({
  bpm: BpmSchema,
  swing: z.number().min(0).max(0.5),
  masterVolumeDb: z.number().min(-60).max(0),
  key: KeySchema,
});
export type ProjectSettings = z.infer<typeof ProjectSettingsSchema>;

function checkUnique<T>(ctx: z.RefinementCtx, items: readonly T[], key: (item: T) => string, path: string, label: string) {
  const seen = new Set<string>();
  items.forEach((item, i) => {
    const k = key(item);
    if (seen.has(k)) ctx.addIssue({ code: 'custom', path: [path, i], message: `Duplicate ${label} "${k}"` });
    seen.add(k);
  });
}

export const ProjectSchema = z
  .object({
    schemaVersion: z.literal(CURRENT_SCHEMA_VERSION),
    id: IdSchema,
    name: NameSchema,
    description: z.string().max(2000),
    createdAt: IsoDateSchema,
    updatedAt: IsoDateSchema,
    settings: ProjectSettingsSchema,
    transport: TransportSettingsSchema,
    tracks: z.array(TrackSchema).min(1).max(INSTRUMENTS.length),
    patterns: z.array(PatternSchema).min(1).max(MAX_PATTERNS),
    activePatternId: IdSchema,
    arrangement: ArrangementSchema,
    /** Parameters of the last procedural composition, kept so it can be reproduced. */
    generator: GeneratorParamsSchema.nullable(),
    scenes: z.array(SceneSchema).min(1).max(MAX_SCENES),
    activeSceneId: IdSchema,
    assets: z.array(AssetSchema).max(1000),
    generationRequests: z.array(GenerationRequestSchema).max(1000),
    outputs: z.array(GenerationOutputSchema).max(1000),
    audioClips: z.array(AudioClipSchema).max(MAX_AUDIO_CLIPS),
    audioBus: AudioBusSchema,
    /** Reusable character definitions, placed in scenes as `character` objects. */
    characters: z.array(CharacterSchema).max(MAX_CHARACTERS),
    /** Keyframe animations (Phase 3B); each belongs to one scene. */
    animations: z.array(AnimationSchema).max(MAX_ANIMATIONS),
    /** Audio-visual sessions (Phase 4A). */
    avSessions: z.array(AvSessionSchema).max(MAX_AV_SESSIONS),
  })
  .superRefine((project, ctx) => {
    if (!project.scenes.some((s) => s.id === project.activeSceneId)) {
      ctx.addIssue({ code: 'custom', path: ['activeSceneId'], message: 'activeSceneId must reference an existing scene' });
    }
    checkUnique(ctx, project.tracks, (t) => t.id, 'tracks', 'track id');
    checkUnique(ctx, project.tracks, (t) => t.instrument, 'tracks', 'track instrument');
    checkUnique(ctx, project.patterns, (p) => p.id, 'patterns', 'pattern id');
    checkUnique(ctx, project.arrangement.sections, (s) => s.id, 'arrangement', 'section id');
    checkUnique(ctx, project.assets, (a) => a.id, 'assets', 'asset id');
    checkUnique(ctx, project.audioClips, (c) => c.id, 'audioClips', 'clip id');
    const audioAssets = new Map(project.assets.filter((a) => a.kind === 'audio').map((a) => [a.id, a]));
    project.tracks.forEach((track, i) => {
      if (!track.sample) return;
      if (!DRUM_INSTRUMENTS.includes(track.instrument)) {
        ctx.addIssue({ code: 'custom', path: ['tracks', i, 'sample'], message: 'Samples can only be assigned to drum and percussion tracks' });
      }
      if (!audioAssets.has(track.sample.assetId)) {
        ctx.addIssue({ code: 'custom', path: ['tracks', i, 'sample', 'assetId'], message: 'Sample references a missing audio asset' });
      }
    });
    project.audioClips.forEach((clip, i) => {
      const asset = audioAssets.get(clip.assetId);
      if (!asset) {
        ctx.addIssue({ code: 'custom', path: ['audioClips', i, 'assetId'], message: 'Clip references a missing audio asset' });
        return;
      }
      // Small tolerance: decoders can disagree by a few samples about a file's length.
      if (asset.audio && clip.offsetSec + clip.durationSec > asset.audio.durationSec + 0.01) {
        ctx.addIssue({ code: 'custom', path: ['audioClips', i, 'durationSec'], message: 'Clip extends past the end of its audio' });
      }
      if (clip.fadeInSec + clip.fadeOutSec > clip.durationSec / clip.playbackRate + 0.001) {
        ctx.addIssue({ code: 'custom', path: ['audioClips', i, 'fadeInSec'], message: 'Fades are longer than the clip' });
      }
    });
    checkUnique(ctx, project.scenes, (s) => s.id, 'scenes', 'scene id');
    checkUnique(ctx, project.characters, (c) => c.id, 'characters', 'character id');
    const imageAssets = new Set(project.assets.filter((a) => a.kind === 'image').map((a) => a.id));
    const characterIds = new Set(project.characters.map((c) => c.id));
    project.scenes.forEach((scene, si) => {
      if (scene.backgroundImage && !imageAssets.has(scene.backgroundImage.assetId)) {
        ctx.addIssue({ code: 'custom', path: ['scenes', si, 'backgroundImage', 'assetId'], message: 'Background references a missing image asset' });
      }
      scene.objects.forEach((o, oi) => {
        if (o.type === 'image' && !imageAssets.has(o.assetId)) {
          ctx.addIssue({ code: 'custom', path: ['scenes', si, 'objects', oi, 'assetId'], message: `Image "${o.name}" references a missing image asset` });
        }
        if (o.type === 'character' && !characterIds.has(o.characterId)) {
          ctx.addIssue({ code: 'custom', path: ['scenes', si, 'objects', oi, 'characterId'], message: `"${o.name}" references a missing character` });
        }
      });
    });
    checkUnique(ctx, project.animations, (a) => a.id, 'animations', 'animation id');
    const scenesById = new Map(project.scenes.map((s) => [s.id, s]));
    const perScene = new Map<string, number>();
    project.animations.forEach((a, ai) => {
      const scene = scenesById.get(a.sceneId);
      if (!scene) {
        ctx.addIssue({ code: 'custom', path: ['animations', ai, 'sceneId'], message: `Animation "${a.name}" references a missing scene` });
        return;
      }
      perScene.set(a.sceneId, (perScene.get(a.sceneId) ?? 0) + 1);
      if (perScene.get(a.sceneId) === MAX_ANIMATIONS_PER_SCENE + 1) {
        ctx.addIssue({ code: 'custom', path: ['animations', ai], message: `A scene can have at most ${MAX_ANIMATIONS_PER_SCENE} animations` });
      }
      const objects = new Map(scene.objects.map((o) => [o.id, o]));
      a.tracks.forEach((t, ti) => {
        const o = objects.get(t.objectId);
        if (!o) ctx.addIssue({ code: 'custom', path: ['animations', ai, 'tracks', ti, 'objectId'], message: `Animation "${a.name}" has a track for a missing object` });
        else if (isPoseChannel(t.channel) && o.type !== 'character') {
          ctx.addIssue({ code: 'custom', path: ['animations', ai, 'tracks', ti, 'channel'], message: `Pose channels only apply to characters ("${o.name}" is a ${o.type})` });
        }
      });
    });
    checkUnique(ctx, project.avSessions, (s) => s.id, 'avSessions', 'session id');
    const animationsById = new Map(project.animations.map((a) => [a.id, a]));
    // Music references (a pattern source, a mapping's section) are soft: music edits may
    // replace patterns and sections, so a missing pattern plays the song and a missing
    // section matches no events. Scene, animation and object references are strict.
    project.avSessions.forEach((session, si) => {
      const at = (path: (string | number)[], message: string) => ctx.addIssue({ code: 'custom', path: ['avSessions', si, ...path], message: `Session "${session.name}": ${message}` });
      const scene = scenesById.get(session.sceneId);
      if (!scene) return at(['sceneId'], 'references a missing scene');
      if (session.animationId !== null) {
        const a = animationsById.get(session.animationId);
        if (!a) at(['animationId'], 'references a missing animation');
        else if (a.sceneId !== session.sceneId) at(['animationId'], `animation "${a.name}" belongs to another scene`);
      }
      const objects = new Map(scene.objects.map((o) => [o.id, o]));
      session.mappings.forEach((m, mi) => {
        if (m.target.kind !== 'object') return;
        const o = objects.get(m.target.objectId);
        if (!o) at(['mappings', mi, 'target', 'objectId'], `mapping "${m.name}" targets a missing object`);
        else if (m.reaction === 'pose' && o.type !== 'character') at(['mappings', mi, 'reaction'], `pose reactions need a character ("${o.name}" is a ${o.type})`);
      });
      session.motionCues.forEach((c, ci) => {
        if (!objects.has(c.objectId)) at(['motionCues', ci, 'objectId'], 'motion cue targets a missing object');
      });
    });
    const patternIds = new Set(project.patterns.map((p) => p.id));
    if (!patternIds.has(project.activePatternId)) {
      ctx.addIssue({ code: 'custom', path: ['activePatternId'], message: 'activePatternId must reference an existing pattern' });
    }
    project.arrangement.sections.forEach((section, i) => {
      if (!patternIds.has(section.patternId)) {
        ctx.addIssue({ code: 'custom', path: ['arrangement', 'sections', i, 'patternId'], message: 'Section references a missing pattern' });
      }
    });
    const bars = project.arrangement.sections.reduce((sum, s) => sum + s.bars, 0);
    if (bars > MAX_SONG_BARS) {
      ctx.addIssue({ code: 'custom', path: ['arrangement'], message: `Arrangement is ${bars} bars; the limit is ${MAX_SONG_BARS}` });
    }
  });
export type Project = z.infer<typeof ProjectSchema>;

/** Lightweight row shown on the dashboard; derived from a project, never stored separately. */
export interface ProjectSummary {
  id: string;
  name: string;
  description: string;
  updatedAt: string;
  createdAt: string;
  bpm: number;
  trackCount: number;
  sectionCount: number;
  genre: Genre | null;
}

export function summarize(project: Project): ProjectSummary {
  return {
    id: project.id,
    name: project.name,
    description: project.description,
    updatedAt: project.updatedAt,
    createdAt: project.createdAt,
    bpm: project.settings.bpm,
    trackCount: project.tracks.length,
    sectionCount: project.arrangement.sections.length,
    genre: project.generator?.genre ?? null,
  };
}

/** Formats Zod issues into short, human-readable lines for error messages. */
export function formatIssues(error: z.ZodError, limit = 5): string[] {
  const lines = error.issues.slice(0, limit).map((issue) => {
    const path = issue.path.length ? issue.path.join('.') : '(root)';
    return `${path}: ${issue.message}`;
  });
  if (error.issues.length > limit) lines.push(`…and ${error.issues.length - limit} more problem(s)`);
  return lines;
}
